<template>
  <div class="front-details">
    <v-row align="center">
      <v-col>
        <v-breadcrumbs :items="breadcrumb" large></v-breadcrumbs>
      </v-col>
      <v-col class="text-right">
        <v-btn color="primary" icon dark class="mr-4" @click="refresh">
          <v-icon>refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-divider></v-divider>
    <v-banner color="error" dark v-if="fetchOneError">
      <v-icon slot="icon" color="white" size="36">mdi-wifi-strength-alert-outline</v-icon>
      {{ errorDisplay(fetchOneError) }}
      <template v-slot:actions>
        <v-btn text color="dark" @click="refresh">Retry</v-btn>
      </template>
    </v-banner>

    <v-tabs v-model="tab" background-color="primary" dark icons-and-text grow>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-settings">
        <span class="mt-2">Settings</span>
        <v-icon>settings</v-icon>
      </v-tab>
      <v-tab-item value="tab-settings" class="grey lighten-5">
        <v-container>
          <FrontEditionForm
            :uuid="front ? front.uuid : ''"
            :id="front ? front.application_id: ''"
            :name="front ? front.application_name: ''"
            :description="front ? front.application_description: ''"
            :rootUrl="front ? front.root_url: ''"
            :redirectUris="front ? front.redirect_uris: []"
            :webOrigins="front ? front.web_origins: []"
            v-on:save="updateFront"
            ref="frontEditionForm"
          />
        </v-container>
      </v-tab-item>
      <v-tab href="#tab-scopes">
        <span class="mt-2">Scopes</span>
        <v-icon>pages</v-icon>
      </v-tab>
      <v-tab-item value="tab-scopes" class="grey lighten-5">
        <v-container fluid>
          <FrontScopesPicker
            :availableScopes="availableScopes"
            :optionalScopes="optionalScopes"
            :defaultScopes="defaultScopes"
            v-on:AddDefault="addDefaultScopeRequest"
            v-on:addOptional="addOptionalScopeRequest"
            v-on:removeDefault="removeDefaultScopeRequest"
            v-on:removeOptional="removeOptionalScopeRequest"
          />
        </v-container>
      </v-tab-item>
    </v-tabs>
    <v-snackbar v-model="snackbar" top>
      {{ errorMessage }}
      <v-btn color="red" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import mixin from "@/mixins";
import FrontEditionForm from "@/components/Admin/Fronts/FrontEditionForm";
import FrontScopesPicker from "@/components/Admin/Fronts/FrontScopesPicker";

export default {
  mixins: [mixin],
  components: {
    FrontEditionForm,
    FrontScopesPicker
  },
  data() {
    return {
      frontUuid: this.$route.params.frontId,
      front: null,
      snackbar: false,
      errorMessage: "",
      tab: null,
      defaultScopes: [],
      optionalScopes: [],
      availableScopes: []
    };
  },
  computed: {
    ...mapState({
      fetchOneError: state => state.fronts.fetchOne.error,
      fetchOneLoading: state => state.fronts.fetchOne.pending
    }),
    breadcrumb() {
      return [
        {
          text: "Fronts",
          disabled: false,
          href: "#/admin/fronts"
        },
        {
          text: this.front ? this.front.application_id : "unknown",
          disabled: true
        }
      ];
    }
  },
  methods: {
    ...mapActions("fronts", { fetchOne: "fetchOne" }),
    ...mapActions("fronts", { updateOne: "updateOne" }),
    ...mapActions("fronts", { getDefaultScopes: "getDefaultScopes" }),
    ...mapActions("scopes", { fetchAllScopes: "fetchAll" }),
    ...mapActions("fronts", { addDefaultScope: "addDefaultScope" }),
    ...mapActions("fronts", { removeDefaultScope: "removeDefaultScope" }),
    ...mapActions("fronts", { getOptionalScopes: "getOptionalScopes" }),
    ...mapActions("fronts", { addOptionalScope: "addOptionalScope" }),
    ...mapActions("fronts", { removeOptionalScope: "removeOptionalScope" }),
    refresh() {
      this.fetchOne({ uuid: this.frontUuid })
        .then(front => {
          this.front = front;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.refreshScopes();
    },
    refreshScopes() {
      this.getDefaultScopes({ uuid: this.frontUuid })
        .then(scopes => {
          this.defaultScopes = scopes;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.getOptionalScopes({ uuid: this.frontUuid })
        .then(scopes => {
          this.optionalScopes = scopes;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllScopes()
        .then(scopes => {
          this.availableScopes = scopes;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    updateFront(id, name, description, url, redirectUris, webOrigins) {
      this.updateOne({
        uuid: this.$route.params.frontId,
        id: id,
        name: name,
        description: description,
        url: url,
        redirectUris: redirectUris,
        webOrigins: webOrigins
      })
        .then(() => {
          this.refresh();
          this.$refs.frontEditionForm.saveCallback();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    addDefaultScopeRequest(scopeUuid) {
      this.addDefaultScope({ uuid: this.frontUuid, scopeUuid: scopeUuid })
        .then(() => {
          this.refreshScopes();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    addOptionalScopeRequest(scopeUuid) {
      this.addOptionalScope({ uuid: this.frontUuid, scopeUuid: scopeUuid })
        .then(() => {
          this.refreshScopes();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeDefaultScopeRequest(scopeUuid) {
      this.removeDefaultScope({ uuid: this.frontUuid, scopeUuid: scopeUuid })
        .then(() => {
          this.refreshScopes();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeOptionalScopeRequest(scopeUuid) {
      this.removeOptionalScope({ uuid: this.frontUuid, scopeUuid: scopeUuid })
        .then(() => {
          this.refreshScopes();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    }
  },
  created() {
    this.refresh();
  }
};
</script>

<style>
</style>